import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import ResourceService from "@core-ui/ContextServices/ResourceService/ResourceService";
import t from "@ext/localization/locale/translate";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import { GlassToolbarButton, GlassToolbarIcon } from "@ui-kit/GlassToolbar";

interface OpenApiActionsProps {
	editor: Editor;
	node: Node;
	updateAttributes: (attrs: Record<string, unknown>) => void;
}

const OpenApiActions = ({ editor, node, updateAttributes }: OpenApiActionsProps) => {
	const { getBuffer } = ResourceService.value;
	const editTooltip = t("edit2");
	const infoTooltip = t(node.attrs.showInfo ? "hide-openapi-info" : "show-openapi-info");
	// One label for both states: the pressed state says whether the block is shown, the tooltip says what the
	// control is for. Swapping the text too made the hint read as a different control after every click.
	const schemasTooltip = t("schemas-block");

	const toggleSchemas = () => {
		updateAttributes({ showSchemas: !node.attrs.showSchemas });
	};

	const toggleInfo = () => {
		updateAttributes({ showInfo: !node.attrs.showInfo });
	};

	const openEditor = () => {
		const src = node.attrs.src;
		ModalToOpenService.setValue(ModalToOpen.DiagramEditor, {
			editor,
			content: getBuffer(src).toString(),
			src,
			diagramName: "OpenApi",
			onClose: () => ModalToOpenService.resetValue(),
		});
	};

	return (
		<>
			<GlassToolbarButton
				aria-label={editTooltip}
				data-testid="edit-diagram"
				onClick={openEditor}
				tooltipText={editTooltip}
			>
				<GlassToolbarIcon icon="pencil" />
			</GlassToolbarButton>
			<GlassToolbarButton
				aria-label={infoTooltip}
				aria-pressed={node.attrs.showInfo}
				data-state={node.attrs.showInfo ? "on" : "off"}
				data-testid="toggle-openapi-info"
				onClick={toggleInfo}
				tooltipText={infoTooltip}
			>
				<GlassToolbarIcon icon="heading" />
			</GlassToolbarButton>
			<GlassToolbarButton
				aria-label={schemasTooltip}
				aria-pressed={node.attrs.showSchemas}
				data-state={node.attrs.showSchemas ? "on" : "off"}
				data-testid="toggle-schemas-block"
				onClick={toggleSchemas}
				tooltipText={schemasTooltip}
			>
				<GlassToolbarIcon icon={node.attrs.showSchemas ? "square-check" : "square"} />
			</GlassToolbarButton>
		</>
	);
};

export default OpenApiActions;
